// ═══════════════════════════════════════════════════════════════
// HotWash — Observations List
// Filterable, searchable, clickable, exportable
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState } from 'react';
import {
  Title, Text, TextInput, Select, Group, Paper, Badge,
  SimpleGrid, SegmentedControl, ActionIcon, Modal, Button,
} from '../../components/gab/GabComponents';
import { useDisclosure } from '../../components/gab/GabHooks';

const PAGE_SIZE = 25;
import {
  IconSearch, IconLayoutGrid, IconList, IconTrash, IconChevronDown, IconChevronUp,
} from '@tabler/icons-react';
import { useHotWashStore } from '../../store/hotwashStore';
import { useAuthStore } from '../../store/authStore';
import { TIER_META, TIER_KEYS, CATEGORIES } from '../../types';
import type { Observation } from '../../types';
import ObservationModal from '../../components/hotwash/ObservationModal';
import ExportMenu from '../../components/hotwash/ExportTools';

export default function Observations() {
  const { observations, loadObservations, deleteObservation } = useHotWashStore();
  const { activeIncident, incidents } = useAuthStore();

  const [search, setSearch] = useState('');
  const [tierFilter, setTierFilter] = useState<string>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [incidentFilter, setIncidentFilter] = useState<string>('active');
  const [viewMode, setViewMode] = useState('cards');
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});
  const [page, setPage] = useState(1);

  // Observation detail modal
  const [selectedObs, setSelectedObs] = useState<Observation | null>(null);
  const [obsModalOpened, { open: openObsModal, close: closeObsModal }] = useDisclosure(false);

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState<Observation | null>(null);
  const [deleteOpened, { open: openDelete, close: closeDelete }] = useDisclosure(false);

  useEffect(() => { loadObservations(); }, [loadObservations]);

  useEffect(() => {
    if (activeIncident) setIncidentFilter(String(activeIncident.id));
  }, [activeIncident]);

  // Reset page when filters change
  useEffect(() => { setPage(1); }, [search, tierFilter, categoryFilter, incidentFilter]);

  let filtered = observations;
  if (incidentFilter !== 'all') filtered = filtered.filter(o => String(o.incidentId) === incidentFilter);
  if (tierFilter !== 'all') filtered = filtered.filter(o => o.tier === tierFilter);
  if (categoryFilter !== 'all') filtered = filtered.filter(o => o.category === categoryFilter);
  if (search) {
    const q = search.toLowerCase();
    filtered = filtered.filter(o =>
      (o.title || '').toLowerCase().includes(q) || (o.description || '').toLowerCase().includes(q) || (o.submittedBy || '').toLowerCase().includes(q)
    );
  }
  filtered = [...filtered].sort((a, b) => (b.datecreated ?? '').localeCompare(a.datecreated ?? ''));
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    await deleteObservation(deleteTarget.id);
    closeDelete();
    setDeleteTarget(null);
  };

  const incidentOptions = [
    { value: 'all', label: 'All Incidents' },
    ...incidents.filter(i => i.id != null).map(i => ({ value: String(i.id), label: i.name })),
  ];

  const tierOptions = [
    { value: 'all', label: 'All Tiers' },
    ...TIER_KEYS.map(t => ({ value: t, label: TIER_META[t].label })),
  ];

  const catOptions = [
    { value: 'all', label: 'All Categories' },
    ...CATEGORIES.map(c => ({ value: c, label: c.charAt(0).toUpperCase() + c.slice(1) })),
  ];

  return (
    <div>
      <Group justify="space-between" mb="md">
        <Title order={3} c="black">Observations</Title>
        <ExportMenu observations={filtered as any} incidents={incidents as any} />
      </Group>

      {/* Filters */}
      <Group mb="md" gap="sm" wrap="wrap">
        <TextInput
          placeholder="Search observations..."
          leftSection={<IconSearch size={14} />}
          value={search}
          onChange={e => setSearch(e.target.value)}
          size="xs"
          w={220}
        />
        <Select data={incidentOptions} value={incidentFilter} onChange={v => setIncidentFilter(v ?? 'all')} size="xs" w={180} />
        <Select data={tierOptions} value={tierFilter} onChange={v => setTierFilter(v ?? 'all')} size="xs" w={160} />
        <Select data={catOptions} value={categoryFilter} onChange={v => setCategoryFilter(v ?? 'all')} size="xs" w={160} />
        <SegmentedControl
          size="xs"
          value={viewMode}
          onChange={setViewMode}
          data={[
            { value: 'cards', label: <IconLayoutGrid size={14} /> },
            { value: 'table', label: <IconList size={14} /> },
          ]}
        />
      </Group>

      <Text size="sm" c="dimmed" mb="sm">{filtered.length} observation{filtered.length !== 1 ? 's' : ''}</Text>

      {filtered.length === 0 ? (
        <Paper p="xl" radius="sm" withBorder>
          <Text ta="center" c="dimmed">No observations match your filters.</Text>
        </Paper>
      ) : viewMode === 'cards' ? (
        <div style={{ overflowY: 'auto', maxHeight: 'calc(100vh - 320px)' }}>
        <SimpleGrid cols={{ base: 1, md: 2 }}>
          {paged.map(obs => {
            const meta = TIER_META[obs.tier] ?? { label: obs.tier || 'Unknown', color: '#888', bg: '#f5f5f5' };
            const isExpanded = expanded[obs.id];
            const inc = incidents.find(i => String(i.id) === String(obs.incidentId));
            return (
              <Paper
                key={obs.id} p="sm" radius="sm" withBorder
                style={{ borderLeft: `3px solid ${meta.color}`, cursor: 'pointer' }}
                onClick={() => { setSelectedObs(obs as any); openObsModal(); }}
              >
                <Group justify="space-between" wrap="nowrap" mb={4}>
                  <Group gap="xs">
                    <Badge size="xs" style={{ background: meta.bg, color: meta.color }}>{meta.label}</Badge>
                    <Badge size="xs" variant="outline" color="#939598" tt="capitalize">{obs.category}</Badge>
                    <Badge size="xs" variant="outline" color="#939598" tt="capitalize">{obs.phase}</Badge>
                  </Group>
                  <Group gap={4}>
                    <ActionIcon size="xs" variant="subtle" color="#939598" onClick={e => { e.stopPropagation(); setExpanded(prev => ({ ...prev, [obs.id]: !prev[obs.id] })); }}>
                      {isExpanded ? <IconChevronUp size={12} /> : <IconChevronDown size={12} />}
                    </ActionIcon>
                    <ActionIcon size="xs" variant="subtle" color="var(--color-error)" onClick={e => { e.stopPropagation(); setDeleteTarget(obs); openDelete(); }}>
                      <IconTrash size={12} />
                    </ActionIcon>
                  </Group>
                </Group>
                <Text size="sm" fw={500}>{obs.title}</Text>
                <Text size="xs" c="dimmed" lineClamp={isExpanded ? undefined : 2}>{obs.description}</Text>
                {isExpanded && obs.suggestedRemedy && (
                  <Paper p="xs" mt="xs" radius="sm" bg="var(--success-light)" style={{ border: '1px solid var(--success)' }}>
                    <Text size="xs" fw={600} mb={2}>Suggested Remedy</Text>
                    <Text size="xs">{obs.suggestedRemedy}</Text>
                  </Paper>
                )}
                <Group justify="space-between" mt="xs">
                  <Group gap="xs">
                    <Text size="xs" c="dimmed">{obs.submittedBy}</Text>
                    {inc && <Text size="xs" c="dimmed">| {inc.name}</Text>}
                  </Group>
                  <Text size="xs" c="dimmed">{obs.datecreated ? new Date(obs.datecreated).toLocaleDateString() : ''}</Text>
                </Group>
              </Paper>
            );
          })}
        </SimpleGrid>
        </div>
      ) : (
        <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 320px)' }}>
        <Paper radius="sm" withBorder style={{ overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ background: 'var(--bg-secondary)', borderBottom: 'var(--border)' }}>
                <th style={{ padding: '8px 16px', textAlign: 'left' }}>Tier</th>
                <th style={{ padding: '8px 16px', textAlign: 'left' }}>Title</th>
                <th style={{ padding: '8px 16px', textAlign: 'left' }}>Category</th>
                <th style={{ padding: '8px 16px', textAlign: 'left' }}>Phase</th>
                <th style={{ padding: '8px 16px', textAlign: 'left' }}>Submitted By</th>
                <th style={{ padding: '8px 16px', textAlign: 'left' }}>Incident</th>
                <th style={{ padding: '8px 16px', textAlign: 'left' }}>Date</th>
                <th style={{ padding: '8px 16px' }}></th>
              </tr>
            </thead>
            <tbody>
              {paged.map(obs => {
                const meta = TIER_META[obs.tier] ?? { label: obs.tier || 'Unknown', color: '#888', bg: '#f5f5f5' };
                const inc = incidents.find(i => String(i.id) === String(obs.incidentId));
                return (
                  <tr
                    key={obs.id}
                    style={{ borderBottom: '1px solid var(--frost)', cursor: 'pointer' }}
                    onClick={() => { setSelectedObs(obs as any); openObsModal(); }}
                  >
                    <td style={{ padding: '8px 16px' }}>
                      <Badge size="xs" style={{ background: meta.bg, color: meta.color }}>{meta.label}</Badge>
                    </td>
                    <td style={{ padding: '8px 16px' }}>{obs.title}</td>
                    <td style={{ padding: '8px 16px', textTransform: 'capitalize' }}>{obs.category}</td>
                    <td style={{ padding: '8px 16px', textTransform: 'capitalize' }}>{obs.phase}</td>
                    <td style={{ padding: '8px 16px' }}>{obs.submittedBy}</td>
                    <td style={{ padding: '8px 16px' }}>{inc?.name ?? ''}</td>
                    <td style={{ padding: '8px 16px' }}>{obs.datecreated ? new Date(obs.datecreated).toLocaleDateString() : ''}</td>
                    <td style={{ padding: '8px 16px' }}>
                      <ActionIcon size="xs" variant="subtle" color="var(--color-error)" onClick={e => { e.stopPropagation(); setDeleteTarget(obs); openDelete(); }}>
                        <IconTrash size={12} />
                      </ActionIcon>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Paper>
        </div>
      )}

      {totalPages > 1 && (
        <Group justify="center" mt="md" gap="xs">
          <Button variant="subtle" size="xs" disabled={page === 1} onClick={() => setPage(1)}>««</Button>
          <Button variant="subtle" size="xs" disabled={page === 1} onClick={() => setPage(p => p - 1)}>‹ Prev</Button>
          {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
            const p = totalPages <= 7 ? i + 1 : page <= 4 ? i + 1 : page >= totalPages - 3 ? totalPages - 6 + i : page - 3 + i;
            return <Button key={p} variant={p === page ? 'filled' : 'subtle'} color="var(--color-primary-700)" size="xs" onClick={() => setPage(p)}>{p}</Button>;
          })}
          <Button variant="subtle" size="xs" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>Next ›</Button>
          <Button variant="subtle" size="xs" disabled={page === totalPages} onClick={() => setPage(totalPages)}>»»</Button>
        </Group>
      )}

      {/* Observation Detail Modal */}
      <ObservationModal
        observation={selectedObs}
        opened={obsModalOpened}
        onClose={closeObsModal}
        onDelete={(id) => deleteObservation(id)}
      />

      {/* Delete Confirmation */}
      <Modal opened={deleteOpened} onClose={closeDelete} title="Delete Observation" size="sm">
        <Text size="sm" mb="md">Delete "{deleteTarget?.title}"? This cannot be undone.</Text>
        <Group justify="flex-end">
          <Button variant="subtle" size="xs" onClick={closeDelete}>Cancel</Button>
          <Button color="var(--color-error)" size="xs" onClick={handleDelete}>Delete</Button>
        </Group>
      </Modal>
    </div>
  );
}
